// eslint-disable-next-line no-unused-vars
import React from 'react';
import { Link } from 'react-router-dom';
import { array, bool, func, object, number } from 'prop-types';
import { ModalBody, ComposedModal, TextArea, Button, Loading } from '@carbon/react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@emotion/react';
import ReCAPTCHA from 'react-google-recaptcha';

import ModalManager from 'Containers/ModalManager/ModalManager';
import useRouter from 'Hooks/useRouter';
import { NWTextInput, NWComboBox, NWCheckbox, NWNotification } from 'Components/shared';
import { NETWORK_ONE_DISTRIBUTION } from 'Constants';

import LoginBackground from 'Assets/images/login-bg2.jpg';
import Logo from 'Assets/images/logo-nod.png';
import Logo2x from 'Assets/images/logo-nod@2x.png';

const StyledComposedModal = styled(ComposedModal)`
  width: 100%;
  height: 100%;
  z-index: 10000 !important;
  background: #0F233C no-repeat center url(${ LoginBackground });
  background-size: cover;

  a {
    color: #0f62fe
  }

  .cds--checkbox-label-text button {
    background: transparent;
    border: none;
    cursor: pointer;
    color: #0f62fe;
    font-size: inherit;
    padding: 0;
  }

  .companyAddress .cds--form-item .cds--label:after {
    content: '*';
    display: inline-block;
    margin-left: 5px;
    color: #D52B1E;
  }

  .cds--inline-notification {
    background-color: transparent;
    color: #3A3A3A;
    max-width: 100%;
    border: none;

    .cds--inline-notification__details {
      margin: 0;
    }

    &.cds--inline-notification--error {
      background: #FFF2F1;
    }

    p {
      padding-right: 0;
      font-size: 14px;
    }

    .cds--inline-notification__close-button .cds--inline-notification__close-icon {
      fill: #3A3A3A;
    }
  }

  .responseMessage {
    p {
      padding-right: 0;
    }

    .messages .message {
      padding: 10px;
    }
  }

  .submit {
    width: 100%;
    height: 40px;
    max-width: none;
    margin-top: 24px;
    padding: 0;
    justify-content: center;
    font-size: 1rem;
    text-align: center;

    .cds--loading {
      left: calc(50% - 75px);
    }

    svg {
      margin-right: 5px;
    }
  }

  .partnerShip {
    padding: 48px 25px 40px;
    margin-top: 5px;
    display: flex;
    justify-content: center;

    p { 
      padding-right: 0;
      
      a {
        outline: none;
      }
    }
  }

  .cds--modal-container {
    max-width: 680px;
    box-shadow: none;
    max-height: none;
    height: auto;
    background-color: #fffffff2;
    overflow: hidden;
    max-height: calc(100vh - 60px);
  }

  .cds--checkbox-label-text p {
    padding-right: 0;
  }

  .textarea {
    padding: 12px;
    border: 1px solid ${ props => props.theme.products.colors.border };
    font-size: 0.875rem;
    line-height: 1.375rem;
    color: ${ props => props.theme.colors.darkGray };
    background-color: #ffffff;
    border-radius: 3px;
    border-bottom: none;
    resize: none;

    p {
      font-size: 0.875rem;
      line-height: 1.375rem;

      .code {
        margin-right: 3px;
      }
    }
  }

  .messagePlaceholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 28px;
    text-align: center;

    .title {
      font-size: 1.75rem;
      color: inherit;
      width: 100%;
      font-weight: 700;
    }

    p {
      font-size: 0.875rem;
      line-height: 1.25rem;
      padding: 0;
    }
  }

  .logoContainer {
    width: 100%;
    min-width: 215px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 64px 20px 40px;

    img {
      display: block;
      max-width: 150px;
      height: auto;
    }
  }

  .formContainer {
    padding: 26px 126px;

    .cds--text-input-wrapper {
      margin-top: 24px;
    }

    .subTitle {
      font-size: 20px;
      color: inherit;
      width: 100%;
      font-weight: 700;
    }
  }

  .language {
    padding: 20px;
    display: flex;
    justify-content: end;

    .cds--list-box__wrapper {
      width: 90px;
    }

    .cds--list-box__selection {
      display: none;
    }

    .cds--list-box__field .cds--text-input {
      padding-right: 2rem;
    }
  }

  @media screen and (max-width: 767px) {
      .cds--modal-container {
        position: static;
        margin: 15px;
        max-height: calc(100vh - 30px);
      }

      .messagePlaceholder {
        padding: 0 20px;
      }

      .formContainer {
        padding: 26px 20px;
      }

      .partnerShip {
        padding: 20px;
      }

      .logoContainer {
        padding: 40px 20px 32px;
      }
    }
`;

const StyledTextInputSkeleton = styled.div`
  .cds--label {
    &:before {
      background: transparent;
      transform: none;
      transition: none;
      animation: none;
      will-change: unset;
    }

    &:after {
      content: '*';
      display: inline-block;
      margin-left: 5px;
      color: ${ props => props.theme.colors.default };
    }

    background: transparent;
    display: inline-block;
    font-size: 0.875rem;
    line-height: 1rem;
    color: ${ props => props.theme.colors.darkGray };
    width: auto;
    margin: 0 !important;
  }

  margin: 1px 0 15px;
`;

const StyledModalBody = styled(ModalBody)`
  display: flex;
  margin: 0 auto !important;
  padding: 0 !important;
  width: 100%;
  flex-direction: column;
`;

const fields = {
  companyName: 'companyName',
  companyActivity: 'companyActivity',
  companyTaxIdentificationNumber: 'companyTaxIdentificationNumber',
  companyCountry: 'companyCountry',
  companyCounty: 'companyCounty',
  companyCity: 'companyCity',
  companyCountyText: 'companyCountyText',
  companyCityText: 'companyCityText',
  companyAddress: 'companyAddress',
  companyPhone: 'companyPhone',
  companyFax: 'companyFax',
  contactFirstName: 'contactFirstName',
  contactLastName: 'contactLastName',
  contactPosition: 'contactPosition',
  contactEmail: 'contactEmail',
  cspAccount: 'cspAccount',
  mpnId: 'mpnId',
  gdprConsent: 'gdprConsent',
  captcha: 'captcha',
};

const RegistrationModal = ({
  updateForm,
  handleOnChangeCaptcha,
  onSubmit,
  loadingCountries,
  loadingCounties,
  loadingCities,
  companyCountries,
  companyCounties,
  companyCities,
  loading,
  errors,
  romaniaId,
  languages,
  language,
  setLanguage,
  recaptchaRef,
  modalRef,
  companyCountryValue,
  companyCountyValue,
  companyCityValue,
  // cspAccountValue,
  register,
  validators,
  rhfErrors,
  control,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { push } = useRouter();

  const sharedProps = fieldName => ({
    id: fieldName,
    name: fieldName,
    invalid: !!rhfErrors[fieldName]?.message.length,
    invalidText: rhfErrors[fieldName]?.message,
    ...register(fieldName, { ...validators[fieldName] }),
  });

  const sharedComboBoxProps = fieldName => ({
    id: fieldName,
    name: fieldName,
    invalid: rhfErrors[fieldName]?.message.length,
    invalidText: rhfErrors[fieldName]?.message,
    control,
    rules: { ...validators[fieldName] }
  });

  return (
    <ModalManager open>
      <StyledComposedModal
        className="RegistrationModal"
        onClose={ e => e.preventDefault() }
        preventCloseOnClickOutside
        size="md"
        theme={ theme }
      >
        <StyledModalBody theme={ theme }>
          <div className="language">
            <NWComboBox
              label="lang"
              light
              name="lang"
              id="lang"
              initialSelectedItem={ language }
              onChange={ item => setLanguage(item.selectedItem) }
              items={ languages }
            />
          </div>
          <div className="logoContainer" ref={ modalRef }>
            <img
              src={ Logo2x }
              srcSet={ `${Logo} 149w, ${Logo2x} 297w` }
              sizes="(max-width: 600px) 149px, 297px"
              alt={ NETWORK_ONE_DISTRIBUTION }
            />
          </div>
          <div className="messagePlaceholder">
            <h3 className="title">{ t('profile.registrationTitle') }</h3>
            <p>{ t('profile.registrationSubTitle') }</p>
          </div>
          <div className="formContainer">
            <div className="responseMessage">
              <NWNotification errors={ errors } />
            </div>
            <div className="subForm">
              <h3 className="subTitle">{ t('profile.company') }</h3>
              <NWTextInput
                light
                type="text"
                required
                labelText={ t('profile.companyName') }
                { ...sharedProps(fields.companyName) }
                onChange={ e => updateForm(fields.companyName, e.target.value) }
              />
              <NWTextInput
                light
                type="text"
                required
                labelText={ t('profile.companyActivity') }
                { ...sharedProps(fields.companyActivity) }
                onChange={ e => updateForm(fields.companyActivity, e.target.value) }
              />
              <NWTextInput
                light
                type="text"
                required
                labelText={ t('profile.companyTaxIdentificationNumber') }
                { ...sharedProps(fields.companyTaxIdentificationNumber) }
                onChange={ e => updateForm(fields.companyTaxIdentificationNumber, e.target.value) }
              />
              {
                loadingCountries
                  ? (
                    <StyledTextInputSkeleton theme={ theme }>
                      <div className="cds--form-item">
                        <span className="cds--label cds--skeleton">
                          { t('profile.companyCountry') }
                        </span>
                        <div className="cds--skeleton cds--text-input" />
                      </div>
                    </StyledTextInputSkeleton>
                  ) : (
                    <NWComboBox
                      titleText={ t('profile.companyCountry') }
                      placeholder={ `${ t('common.select') } ${ t('order.dropShippingData.country') }` }
                      autoComplete="none"
                      light
                      required
                      disabled={ !companyCountries }
                      { ...sharedComboBoxProps(fields.companyCountry) }
                      onChange={ value => updateForm(fields.companyCountry, value) }
                      items={ companyCountries || [] }
                      selectedItem={
                        (companyCountries || []).find(country => country?.id === companyCountryValue) || null
                      }
                    />
                  )
              }
              { companyCountryValue === romaniaId ? (
                <>
                  {
                    loadingCounties
                      ? (
                        <StyledTextInputSkeleton theme={ theme }>
                          <div className="cds--form-item">
                            <span className="cds--label cds--skeleton">
                              { t('profile.companyCounty') }
                            </span>
                            <div className="cds--skeleton cds--text-input" />
                          </div>
                        </StyledTextInputSkeleton>
                      ) : (
                        <NWComboBox
                          titleText={ t('profile.companyCounty') }
                          placeholder={ `${ t('common.select') } ${ t('order.dropShippingData.county') }` }
                          autoComplete="none"
                          light
                          required
                          { ...sharedComboBoxProps(fields.companyCounty) }
                          onChange={ value => updateForm(fields.companyCounty, value) }
                          items={ companyCounties || [] }
                          selectedItem={
                            (companyCounties || []).find(county => county?.erpCountyId === companyCountyValue) || ''
                          }
                        />
                      )
                  }
                  {
                    loadingCities
                      ? (
                        <StyledTextInputSkeleton theme={ theme }>
                          <div className="cds--form-item">
                            <span className="cds--label cds--skeleton">
                              { t('profile.companyCity') }
                            </span>
                            <div className="cds--skeleton cds--text-input" />
                          </div>
                        </StyledTextInputSkeleton>
                      ) : (
                        <NWComboBox
                          titleText={ t('profile.companyCity') }
                          placeholder={ `${ t('common.select') } ${ t('order.dropShippingData.city') }` }
                          autoComplete="none"
                          light
                          required
                          disabled={ !companyCities }
                          { ...sharedComboBoxProps(fields.companyCity) }
                          onChange={ value => updateForm(fields.companyCity, value) }
                          items={ companyCities || [] }
                          selectedItem={
                            (companyCities || []).find(city => city?.name === companyCityValue) || ''
                          }
                        />
                      )
                  }
                </>
              ) : (
                <>
                  <NWTextInput
                    light
                    type="text"
                    required
                    labelText={ t('profile.companyCounty') }
                    { ...sharedProps(fields.companyCountyText) }
                    onChange={ e => updateForm(fields.companyCountyText, e.target.value) }
                  />
                  <NWTextInput
                    light
                    type="text"
                    required
                    labelText={ t('profile.companyCity') }
                    { ...sharedProps(fields.companyCityText) }
                    onChange={ e => updateForm(fields.companyCityText, e.target.value) }
                  />
                </>
              )}
              <div className="companyAddress">
                <TextArea
                  className="companyAddress textarea"
                  name={ t('profile.companyAddress') }
                  light
                  type="text"
                  required
                  labelText={ t('profile.companyAddress') }
                  { ...sharedProps(fields.companyAddress) }
                  onChange={ e => updateForm(fields.companyAddress, e.target.value) }
                />
              </div>
              <NWTextInput
                light
                type="text"
                required
                labelText={ t('profile.companyPhone') }
                { ...sharedProps(fields.companyPhone) }
                onChange={ e => updateForm(fields.companyPhone, e.target.value) }
              />
              <NWTextInput
                light
                type="text"
                labelText={ t('profile.companyFax') }
                { ...sharedProps(fields.companyFax) }
                onChange={ e => updateForm(fields.companyFax, e.target.value) }
              />
            </div>
            <div className="subForm">
              <h3 className="subTitle">{ t('profile.contact') }</h3>
              <NWTextInput
                light
                type="text"
                required
                labelText={ t('profile.contactFirstName') }
                { ...sharedProps(fields.contactFirstName) }
                onChange={ e => updateForm(fields.contactFirstName, e.target.value) }
              />
              <NWTextInput
                light
                type="text"
                required
                labelText={ t('profile.contactLastName') }
                { ...sharedProps(fields.contactLastName) }
                onChange={ e => updateForm(fields.contactLastName, e.target.value) }
              />
              <NWTextInput
                light
                type="text"
                required
                labelText={ t('profile.contactPosition') }
                { ...sharedProps(fields.contactPosition) }
                onChange={ e => updateForm(fields.contactPosition, e.target.value) }
              />
              <NWTextInput
                light
                type="text"
                required
                labelText={ t('profile.contactEmail') }
                { ...sharedProps(fields.contactEmail) }
                onChange={ e => updateForm(fields.contactEmail, e.target.value) }
              />
            </div>
            {/* <Checkbox
              id="cspAccount"
              name="cspAccount"
              labelText={ <p>...</p> }
              onChange={ checked => updateForm(fields.cspAccount, checked) }
            /> */}
            {/* { cspAccountValue && (
              <NWTextInput
                light
                type="text"
                labelText={ t('profile.mpnId') }
                { ...sharedProps(fields.mpnId) }
                onChange={ e => updateForm(fields.mpnId, e.target.value) }
              />
            ) } */}
            <NWCheckbox
              id="gdprConsent"
              name="gdprConsent"
              labelText={ <p>{t('profile.gdpr.agree')} <button type="button" onClick={ e => { e.stopPropagation(); push('/information/data-processing-policy'); } }> {t('profile.gdpr.policy')}</button></p> }
              { ...sharedProps(fields.gdprConsent) }
              onChange={ checked => updateForm(fields.gdprConsent, checked) }
            />
            <ReCAPTCHA
              ref={ recaptchaRef }
              size="invisible"
              sitekey="6LcIb9IrAAAAAEfM5CFzdQlSjbJeYqMNjMJhmIET"
              onChange={ handleOnChangeCaptcha }
            />
            <Button
              className="submit"
              type="submit"
              disabled={ loading }
              onClick={ onSubmit }
              kind="danger"
            >
              { loading && <Loading withOverlay={ false } small active /> }
              { t('profile.register') }
            </Button>
            <div className="partnerShip">
              <p><Link to="/login">{ t('profile.alreadyMember') }</Link></p>
            </div>
          </div>
        </StyledModalBody>
      </StyledComposedModal>
    </ModalManager>
  );
};

RegistrationModal.propTypes = {
  updateForm: func,
  handleOnChangeCaptcha: func,
  onSubmit: func,
  loadingCountries: bool,
  loadingCounties: bool,
  loadingCities: bool,
  companyCountries: array,
  companyCounties: array,
  companyCities: array,
  loading: bool,
  errors: array,
  romaniaId: number,
  languages: array,
  language: object,
  setLanguage: func,
  recaptchaRef: object,
  modalRef: object,
  companyCountryValue: number,
  companyCountyValue: number,
  companyCityValue: number,
  // cspAccountValue: bool,
  register: func,
  validators: object,
  rhfErrors: object,
  control: object,
};

RegistrationModal.displayName = 'RegistrationModalComponent';

export default RegistrationModal;
